// Platform Admin — 5-step guided workflow creation wizard (Identify → Starting point → Scaffold
// Stages/Processes/Statuses → Transitions & gates → Validate & publish). Replaces the previous
// direct-to-editor create flow at /platform/workflows/new. Ongoing edits to an existing draft
// still go through WorkflowEditorPage — this page only covers first-time creation.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { StatCard } from '@/components/UI/StatCard';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { Chip } from '@/components/UI/Chip';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import {
  useListWorkflowDefinitionsQuery,
  useCloneWorkflowDefinitionMutation,
  useCreateWorkflowDefinitionMutation,
  useUpdateWorkflowDefinitionMutation,
  usePublishWorkflowDefinitionMutation,
  CreateWorkflowRequest,
  StageDefinition,
  ProcessDefinition,
  StatusDefinition,
  TransitionDefinition,
  GovernanceGate,
  AuthorityRule,
} from '@/redux/internal-services/workflowApi';
import { useListCatalogueEntriesQuery } from '@/redux/internal-services/platformAdminApi';
import { validateWorkflowDraft } from './workflowValidation';

// ─────────────────────────── Shared UI (matches WorkflowEditorPage's local primitives) ────────

const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label className="block text-xs font-semibold text-ui-text2 mb-1">
    {children}{required && <span className="text-danger-text ml-1">*</span>}
  </label>
);
const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className, ...props }) => (
  <input {...props} className={cn(fieldClass(), className)} />
);
const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, ...props }) => (
  <select {...props} className={cn(fieldClass(), className)} />
);
const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className, ...props }) => (
  <textarea {...props} className={cn(fieldClass({ shape: 'box' }), 'resize-y min-h-20', className)} />
);

const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// ─────────────────────────── Step definitions ─────────────────────────────

type StepId = 'identify' | 'starting_point' | 'scaffold' | 'transitions' | 'validate';

const STEPS: Array<{ id: StepId; title: string; detail: string }> = [
  { id: 'identify', title: 'Identify the workflow', detail: 'name & engagement type' },
  { id: 'starting_point', title: 'Choose a starting point', detail: 'blank · clone · base template' },
  { id: 'scaffold', title: 'Scaffold Stages → Statuses', detail: 'mark entry & terminal' },
  { id: 'transitions', title: 'Add transitions & gates', detail: 'in the builder' },
  { id: 'validate', title: 'Validate & publish v1', detail: 'tenants adopt the published version' },
];

interface WizardDraft {
  name: string;
  description: string;
  engagement_types: string[];
  stages: StageDefinition[];
  processes: ProcessDefinition[];
  statuses: StatusDefinition[];
  transitions: TransitionDefinition[];
  governance_gates: GovernanceGate[];
  authority_rules: AuthorityRule[];
}

const emptyDraft = (): WizardDraft => ({
  name: '', description: '', engagement_types: [],
  stages: [], processes: [], statuses: [], transitions: [], governance_gates: [], authority_rules: [],
});

const ROLES = ['sales_agent', 'project_manager', 'office_admin', 'crew_member', 'tenant_admin'];
const CATEGORIES = ['standard', 'backward', 'skip', 'hold', 'resume', 'reopen', 'cancel', 'reject', 'corrective_loop'];

// ─────────────────────────── Left rail ────────────────────────────────────

const StepRail: React.FC<{ currentIndex: number }> = ({ currentIndex }) => (
  <div className="w-64 shrink-0">
    <p className="eyebrow mb-3">How to create a workflow</p>
    <div className="space-y-1">
      {STEPS.map((s, i) => {
        const done = i < currentIndex;
        const active = i === currentIndex;
        return (
          <div key={s.id} className={`flex gap-3 px-3 py-2.5 rounded-control ${active ? 'bg-accent-soft' : ''}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold mt-0.5 ${
              done ? 'bg-success-fill text-white' : active ? 'bg-accent-fill text-on-accent' : 'bg-ui-subtle text-ui-text3'
            }`}>
              {done ? '✓' : i + 1}
            </span>
            <div className="min-w-0">
              <p className={`text-sm font-medium ${active ? 'text-accent-text' : 'text-ui-text'}`}>{s.title}</p>
              <p className="text-xs text-ui-text3">{s.detail}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─────────────────────────── Step 1: Identify ─────────────────────────────

interface EngagementTypeOption { id: string; label: string }

const IdentifyStep: React.FC<{ draft: WizardDraft; setDraft: React.Dispatch<React.SetStateAction<WizardDraft>> }> = ({ draft, setDraft }) => {
  const { data: catalogueData, isLoading: loadingTypes } = useListCatalogueEntriesQuery({ prefix: 'workflow_engine' });
  const engagementTypeOptions = useMemo<EngagementTypeOption[]>(() => {
    const entry = catalogueData?.data.find((c) => c.key === 'workflow_engine');
    const list = entry?.effective_value?.engagement_types as EngagementTypeOption[] | undefined;
    return list ?? [];
  }, [catalogueData]);

  const toggleType = (id: string) => {
    setDraft((d) => ({
      ...d,
      engagement_types: d.engagement_types.includes(id)
        ? d.engagement_types.filter((x) => x !== id)
        : [...d.engagement_types, id],
    }));
  };

  return (
    <div className="max-w-xl space-y-5">
      <p className="text-sm text-ui-text2">Every engagement running this workflow holds one primary Status from it.</p>
      <div>
        <Label required>Workflow name</Label>
        <Input
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          placeholder="e.g. Roofing — Replacement Project"
        />
        <p className="text-xs text-ui-text3 mt-1">
          The workflow ID is generated automatically from this name{draft.name ? ` (e.g. ${slug(draft.name)})` : ''} — it can't be edited later.
        </p>
      </div>
      <div>
        <Label required>Engagement types — select one or more</Label>
        {loadingTypes ? (
          <p className="text-xs text-ui-text3">Loading engagement types…</p>
        ) : engagementTypeOptions.length === 0 ? (
          <p className="text-xs text-ui-text3">No engagement types configured. Add them under the platform configuration catalogue (workflow_engine).</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {engagementTypeOptions.map((opt) => {
              const selected = draft.engagement_types.includes(opt.id);
              return (
                <Chip key={opt.id} selected={selected} onClick={() => toggleType(opt.id)}>
                  {opt.label}
                </Chip>
              );
            })}
          </div>
        )}
      </div>
      <div>
        <Label>Description (optional)</Label>
        <Textarea value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} rows={3} placeholder="Full tear-off and re-roof lifecycle with staged materials and multi-milestone billing…" />
      </div>
    </div>
  );
};

// ─────────────────────────── Step 2: Starting point ───────────────────────

type StartingPoint = 'blank' | 'clone' | 'template';

const StartingPointStep: React.FC<{
  startingPoint: StartingPoint; setStartingPoint: (s: StartingPoint) => void;
  cloneSourceId: string; setCloneSourceId: (id: string) => void;
  copyGates: boolean; setCopyGates: (v: boolean) => void;
  copyAuthority: boolean; setCopyAuthority: (v: boolean) => void;
  copyHooks: boolean; setCopyHooks: (v: boolean) => void;
}> = ({ startingPoint, setStartingPoint, cloneSourceId, setCloneSourceId, copyGates, setCopyGates, copyAuthority, setCopyAuthority, copyHooks, setCopyHooks }) => {
  const { data: publishedData } = useListWorkflowDefinitionsQuery({ status: 'published' }, { skip: startingPoint !== 'clone' });
  const { data: templateData } = useListWorkflowDefinitionsQuery({ status: 'published', is_template: true }, { skip: startingPoint !== 'template' });
  const sourceList = startingPoint === 'template' ? templateData?.data ?? [] : publishedData?.data ?? [];

  const OPTIONS: Array<{ id: StartingPoint; title: string; desc: string }> = [
    { id: 'blank', title: 'Blank', desc: 'Start with an empty structure and build every Stage, Status and Transition yourself.' },
    { id: 'clone', title: 'Clone existing', desc: 'Copy a published definition, then adjust. Fastest for a close variant.' },
    { id: 'template', title: 'Base template', desc: 'Start from the platform reference lifecycle for this engagement type.' },
  ];

  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-ui-text2">Whichever you pick, the result is an editable draft — nothing is published yet.</p>
      <div className="space-y-2">
        {OPTIONS.map((opt) => (
          <label
            key={opt.id}
            className={`flex items-start gap-3 p-3 rounded-control border cursor-pointer transition ${
              startingPoint === opt.id ? 'border-accent bg-accent-soft' : 'border-ui-border hover:bg-ui-hover'
            }`}
          >
            <input type="radio" className="mt-1" checked={startingPoint === opt.id} onChange={() => setStartingPoint(opt.id)} />
            <div>
              <p className="text-sm font-medium text-ui-text">{opt.title}</p>
              <p className="text-xs text-ui-text3">{opt.desc}</p>
            </div>
          </label>
        ))}
      </div>

      {(startingPoint === 'clone' || startingPoint === 'template') && (
        <div className="pt-2 space-y-3">
          <Label required>{startingPoint === 'template' ? 'Template' : 'Clone source'}</Label>
          <Select value={cloneSourceId} onChange={(e) => setCloneSourceId(e.target.value)}>
            <option value="">Select…</option>
            {sourceList.map((d) => (
              <option key={`${d.definition_id}@${d.version}`} value={`${d.definition_id}@${d.version}`}>
                {d.name} · v{d.version} · {d.status_count} statuses · {d.transition_count} transitions
              </option>
            ))}
          </Select>
          {sourceList.length === 0 && (
            <p className="text-xs text-ui-text3">
              {startingPoint === 'template' ? 'No platform templates published yet.' : 'No published workflows available to clone yet.'}
            </p>
          )}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="checkbox" checked={copyGates} onChange={(e) => setCopyGates(e.target.checked)} /> Copy governance gates
            </label>
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="checkbox" checked={copyAuthority} onChange={(e) => setCopyAuthority(e.target.checked)} /> Copy authority rules
            </label>
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="checkbox" checked={copyHooks} onChange={(e) => setCopyHooks(e.target.checked)} /> Copy automation hooks
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────── Step 3: Scaffold ─────────────────────────────

// Status-card visual treatment — border/badge color driven by operational_type (matches the
// STATUS_BADGE convention used on WorkflowDefinitionPage), with lifecycle_role as a colored
// left accent so ENTRY/TERMINAL read at a glance without another row of text.
const OP_STYLES: Record<StatusDefinition['operational_type'], { badge: BadgeProps['variant']; ring?: string }> = {
  normal:    { badge: 'neutral' },
  on_hold:   { badge: 'warning' },
  closed:    { badge: 'success', ring: 'border-success bg-success/5' },
  cancelled: { badge: 'warning' },
  rejected:  { badge: 'danger' },
};

const StatusCard: React.FC<{ status: StatusDefinition; onRemove: () => void }> = ({ status, onRemove }) => {
  const op = OP_STYLES[status.operational_type];
  const entryRing = status.lifecycle_role === 'entry' ? 'border-accent ring-1 ring-accent/30' : '';
  const terminalRing = status.lifecycle_role === 'terminal' ? (op.ring ?? 'border-danger') : '';
  return (
    <div className={`group relative rounded-control border p-3 ${entryRing || terminalRing || 'border-ui-border'}`}>
      <IconButton
        size="xs"
        label="Remove status"
        onClick={onRemove}
        className="absolute top-1 right-1 opacity-0 transition hover:text-danger-text focus-visible:opacity-100 group-hover:opacity-100"
      >✕</IconButton>
      <p className="text-sm font-semibold text-ui-text truncate pr-4">{status.name}</p>
      <p className="text-xs text-ui-text3 font-mono truncate">{status.status_code}</p>
      <div className="mt-2 flex flex-wrap gap-1">
        {status.lifecycle_role !== 'intermediate' && (
          <Badge variant="accent" className="text-micro font-semibold uppercase">
            {status.lifecycle_role}
          </Badge>
        )}
        {status.is_optional && (
          <Badge variant="neutral" className="text-micro uppercase">optional</Badge>
        )}
        <Badge variant={op.badge} className="text-micro uppercase">
          {status.operational_type.replace(/_/g, ' ')}
        </Badge>
      </div>
    </div>
  );
};

const ScaffoldStep: React.FC<{ draft: WizardDraft; setDraft: React.Dispatch<React.SetStateAction<WizardDraft>> }> = ({ draft, setDraft }) => {
  const [addingStageFor, setAddingStageFor] = useState(false);
  const [stageForm, setStageForm] = useState({ name: '', description: '' });
  const [addingProcessFor, setAddingProcessFor] = useState<string | null>(null);
  const [processForm, setProcessForm] = useState({ name: '' });
  const [addingStatusFor, setAddingStatusFor] = useState<string | null>(null);
  const [statusForm, setStatusForm] = useState({ name: '', lifecycle_role: 'intermediate' as StatusDefinition['lifecycle_role'], operational_type: 'normal' as StatusDefinition['operational_type'] });

  const addStage = () => {
    if (!stageForm.name.trim()) return;
    const code = slug(stageForm.name);
    setDraft((d) => ({ ...d, stages: [...d.stages, { stage_code: code, name: stageForm.name.trim(), display_order: d.stages.length + 1, description: stageForm.description, is_custom: false, tenant_label_renamable: true }] }));
    setStageForm({ name: '', description: '' });
    setAddingStageFor(false);
  };
  const addProcess = (stageCode: string) => {
    if (!processForm.name.trim()) return;
    const code = slug(processForm.name);
    setDraft((d) => ({ ...d, processes: [...d.processes, { process_code: code, stage_code: stageCode, name: processForm.name.trim(), display_order: d.processes.filter((p) => p.stage_code === stageCode).length + 1, description: '', is_custom: false, tenant_label_renamable: true }] }));
    setProcessForm({ name: '' });
    setAddingProcessFor(null);
  };
  const addStatus = (processCode: string) => {
    if (!statusForm.name.trim()) return;
    const code = slug(statusForm.name);
    setDraft((d) => ({ ...d, statuses: [...d.statuses, { status_code: code, process_code: processCode, name: statusForm.name.trim(), display_order: d.statuses.filter((s) => s.process_code === processCode).length + 1, lifecycle_role: statusForm.lifecycle_role, operational_type: statusForm.operational_type, is_optional: false, is_custom: false, description: '' }] }));
    setStatusForm({ name: '', lifecycle_role: 'intermediate', operational_type: 'normal' });
    setAddingStatusFor(null);
  };
  const removeStatus = (code: string) => setDraft((d) => ({ ...d, statuses: d.statuses.filter((s) => s.status_code !== code) }));

  const entryCount = draft.statuses.filter((s) => s.lifecycle_role === 'entry').length;
  const terminalCount = draft.statuses.filter((s) => s.lifecycle_role === 'terminal').length;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-ui-text2">Each Status needs a stable code and an operational type; mark exactly one entry point per workflow and any terminal statuses.</p>
        {addingStageFor ? (
          <div className="card-surface shrink-0 space-y-2 p-3 w-72">
            <Input value={stageForm.name} onChange={(e) => setStageForm((f) => ({ ...f, name: e.target.value }))} placeholder="Stage name (e.g. Projects)" />
            <Textarea value={stageForm.description} onChange={(e) => setStageForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description (optional)" rows={2} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAddingStageFor(false)}>Cancel</Button>
              <Button size="sm" onClick={addStage}>Add stage</Button>
            </div>
          </div>
        ) : (
          <Button size="sm" className="shrink-0" onClick={() => setAddingStageFor(true)}>+ Add stage</Button>
        )}
      </div>

      <div className="space-y-3">
        {draft.stages.map((stage) => (
          <div key={stage.stage_code} className="card-surface p-4">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-base font-bold text-ui-text">{stage.name}</p>
              <Badge variant="accent" className="text-micro font-semibold uppercase">Stage</Badge>
              {draft.processes.filter((p) => p.stage_code === stage.stage_code).length === 1 && (
                <span className="text-xs text-ui-text3">Process: {draft.processes.find((p) => p.stage_code === stage.stage_code)!.name}</span>
              )}
              <span className="ml-auto shrink-0">
                <Button variant="outline" size="sm" onClick={() => setAddingProcessFor(addingProcessFor === stage.stage_code ? null : stage.stage_code)}>+ Add process</Button>
              </span>
            </div>

            {addingProcessFor === stage.stage_code && (
              <div className="mb-3 flex gap-2">
                <Input value={processForm.name} onChange={(e) => setProcessForm({ name: e.target.value })} placeholder="Process name (e.g. Job Execution)" />
                <Button size="sm" onClick={() => addProcess(stage.stage_code)}>Add</Button>
              </div>
            )}

            <div className="space-y-3">
              {draft.processes.filter((p) => p.stage_code === stage.stage_code).map((process, _i, arr) => (
                <div key={process.process_code}>
                  {arr.length > 1 && (
                    <p className="text-xs text-ui-text3 mb-1.5">Process: {process.name}</p>
                  )}

                  <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2.5">
                    {draft.statuses.filter((s) => s.process_code === process.process_code).map((status) => (
                      <StatusCard key={status.status_code} status={status} onRemove={() => removeStatus(status.status_code)} />
                    ))}

                    {addingStatusFor === process.process_code ? (
                      <div className="col-span-full rounded-control border border-dashed border-ui-border2 p-3 grid grid-cols-2 gap-1.5">
                        <Input className="col-span-2" value={statusForm.name} onChange={(e) => setStatusForm((f) => ({ ...f, name: e.target.value }))} placeholder="Status name (e.g. Job Scheduled)" />
                        <Select value={statusForm.lifecycle_role} onChange={(e) => setStatusForm((f) => ({ ...f, lifecycle_role: e.target.value as StatusDefinition['lifecycle_role'] }))}>
                          <option value="entry">Entry</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="terminal">Terminal</option>
                        </Select>
                        <Select value={statusForm.operational_type} onChange={(e) => setStatusForm((f) => ({ ...f, operational_type: e.target.value as StatusDefinition['operational_type'] }))}>
                          <option value="normal">Normal</option>
                          <option value="on_hold">On Hold</option>
                          <option value="closed">Closed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="rejected">Rejected</option>
                        </Select>
                        <Button variant="outline" size="sm" onClick={() => setAddingStatusFor(null)}>Cancel</Button>
                        <Button size="sm" onClick={() => addStatus(process.process_code)}>Add status</Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setAddingStatusFor(process.process_code)}
                        className="rounded-control border border-dashed border-ui-border2 text-xs text-ui-text3 hover:text-ui-text hover:border-ui-border3 flex items-center justify-center min-h-[4.5rem] transition focus-ring"
                      >
                        + add status
                      </button>
                    )}
                    {draft.statuses.filter((s) => s.process_code === process.process_code).length === 0 && addingStatusFor !== process.process_code && (
                      <p className="col-span-full text-xs text-ui-text3">No statuses yet.</p>
                    )}
                  </div>
                </div>
              ))}
              {draft.processes.filter((p) => p.stage_code === stage.stage_code).length === 0 && (
                <p className="text-xs text-ui-text3">No processes yet — add one above.</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-control border border-ui-border bg-ui-subtle px-4 py-3 flex flex-wrap gap-x-6 gap-y-1.5 text-xs">
        <span className={entryCount === 1 ? 'text-success-text' : 'text-warning-text'}>{entryCount === 1 ? '✓' : '▲'} {entryCount} entry point{entryCount === 1 ? '' : 's'} set</span>
        <span className={terminalCount >= 1 ? 'text-success-text' : 'text-warning-text'}>{terminalCount >= 1 ? '✓' : '▲'} {terminalCount} terminal status{terminalCount === 1 ? '' : 'es'} set</span>
      </div>
    </div>
  );
};

// ─────────────────────────── Step 4: Transitions & gates ──────────────────

const TransitionsStep: React.FC<{ draft: WizardDraft; setDraft: React.Dispatch<React.SetStateAction<WizardDraft>> }> = ({ draft, setDraft }) => {
  const [form, setForm] = useState({ source: '', target: '', label: '', category: 'standard' });
  const [selected, setSelected] = useState<string | null>(null);
  const [gateName, setGateName] = useState('');
  const [gateClass, setGateClass] = useState<'blocking' | 'warning'>('blocking');

  const statusName = (code: string) => draft.statuses.find((s) => s.status_code === code)?.name ?? code;

  const addTransition = () => {
    if (!form.source || !form.target || !form.label.trim()) return;
    const code = `${form.source}_to_${form.target}`;
    setDraft((d) => ({ ...d, transitions: [...d.transitions, {
      transition_code: code, source_status_code: form.source, target_status_code: form.target,
      action_label: form.label.trim(), category: form.category as TransitionDefinition['category'],
      requires_reason: false, is_optional: false, is_custom: false, description: '',
    }] }));
    setForm({ source: '', target: '', label: '', category: 'standard' });
  };

  const selectedTransition = draft.transitions.find((t) => t.transition_code === selected) ?? null;
  const gatesForSelected = draft.governance_gates.filter((g) => g.target_ref === selected);
  const authorityForSelected = draft.authority_rules.find((r) => r.authority_type === 'transition' && r.target_refs.includes(selected ?? ''));

  const addGate = () => {
    if (!selected || !gateName.trim()) return;
    setDraft((d) => ({ ...d, governance_gates: [...d.governance_gates, {
      gate_code: `${selected}_${slug(gateName)}`, scope: 'transition', target_ref: selected,
      classification: gateClass, condition_type: 'required_data', condition_config: {}, description: gateName.trim(), is_optional: false,
    }] }));
    setGateName('');
  };

  const toggleReason = () => {
    if (!selected) return;
    setDraft((d) => ({ ...d, transitions: d.transitions.map((t) => t.transition_code === selected ? { ...t, requires_reason: !t.requires_reason } : t) }));
  };

  const toggleRole = (role: string) => {
    if (!selected) return;
    setDraft((d) => {
      const existing = d.authority_rules.find((r) => r.authority_type === 'transition' && r.target_refs.includes(selected));
      if (existing) {
        const roles = existing.allowed_roles.includes(role) ? existing.allowed_roles.filter((r) => r !== role) : [...existing.allowed_roles, role];
        return { ...d, authority_rules: d.authority_rules.map((r) => r === existing ? { ...r, allowed_roles: roles } : r) };
      }
      return { ...d, authority_rules: [...d.authority_rules, { rule_code: `${selected}_authority`, authority_type: 'transition', target_refs: [selected], allowed_roles: [role], assignment_scope: 'any', self_approval_allowed: true, system_allowed: false, description: '' }] };
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-ui-text2">Connect statuses with categorized transitions and attach the gates that must pass before each move.</p>

      <div className="card-surface p-3 grid grid-cols-2 gap-2">
        <Select value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
          <option value="">From status…</option>
          {draft.statuses.map((s) => <option key={s.status_code} value={s.status_code}>{s.name}</option>)}
        </Select>
        <Select value={form.target} onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}>
          <option value="">To status…</option>
          {draft.statuses.map((s) => <option key={s.status_code} value={s.status_code}>{s.name}</option>)}
        </Select>
        <Input className="col-span-2" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Action label (e.g. Schedule Job)" />
        <Select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
        </Select>
        <Button size="sm" onClick={addTransition}>+ New transition</Button>
      </div>

      <div className="table-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="th-cell">Source → Target</th>
                <th className="th-cell">Category</th>
                <th className="th-cell">Gates</th>
              </tr>
            </thead>
            <tbody>
              {draft.transitions.map((t) => (
                <tr key={t.transition_code} onClick={() => setSelected(t.transition_code)}
                  className={`tr-hover ${selected === t.transition_code ? 'bg-accent-soft' : ''}`}>
                  <td className="td-cell text-xs">{statusName(t.source_status_code)} → {statusName(t.target_status_code)}</td>
                  <td className="td-cell text-xs capitalize">{t.category.replace(/_/g, ' ')}</td>
                  <td className="td-cell text-xs text-ui-text2">{draft.governance_gates.filter((g) => g.target_ref === t.transition_code).length}</td>
                </tr>
              ))}
              {draft.transitions.length === 0 && (
                <tr><td colSpan={3} className="td-cell py-4 text-center text-xs text-ui-text3">No transitions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedTransition && (
        <div className="card-surface p-3 space-y-3">
          <p className="eyebrow">
            Gates on selected transition — {statusName(selectedTransition.source_status_code)} → {statusName(selectedTransition.target_status_code)}
          </p>

          <div className="space-y-1.5">
            {gatesForSelected.map((g) => (
              <div key={g.gate_code} className="flex items-center justify-between rounded-control bg-ui-subtle px-2.5 py-1.5">
                <span className="text-xs text-ui-text">{g.description || g.gate_code}</span>
                <Badge variant={g.classification === 'blocking' ? 'danger' : 'warning'} className="text-micro uppercase">{g.classification}</Badge>
              </div>
            ))}
            {gatesForSelected.length === 0 && <p className="text-xs text-ui-text3">No gates yet.</p>}
          </div>
          <div className="flex gap-2">
            <Input value={gateName} onChange={(e) => setGateName(e.target.value)} placeholder="Gate name (e.g. Deposit recorded)" />
            <Select value={gateClass} onChange={(e) => setGateClass(e.target.value as 'blocking' | 'warning')} className="w-40">
              <option value="blocking">Blocking</option>
              <option value="warning">Warning</option>
            </Select>
            <Button size="sm" onClick={addGate}>+ Add gate</Button>
          </div>

          <div>
            <Label>Authorized roles</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              {ROLES.map((role) => (
                <label key={role} className="flex items-center gap-1.5 text-xs text-ui-text2 cursor-pointer">
                  <input type="checkbox" checked={(authorityForSelected?.allowed_roles ?? []).includes(role)} onChange={() => toggleRole(role)} />
                  <span className="capitalize">{role.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-ui-text2 cursor-pointer">
            <input type="checkbox" checked={selectedTransition.requires_reason} onChange={toggleReason} /> Require reason on this move
          </label>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────── Step 5: Validate & publish ───────────────────

const ValidateStep: React.FC<{ draft: WizardDraft }> = ({ draft }) => {
  const checks = useMemo(() => validateWorkflowDraft({
    statuses: draft.statuses,
    transitions: draft.transitions,
    entryPoints: [],
    automationHooks: [],
  }), [draft.statuses, draft.transitions]);
  const warnings = checks.filter((c) => c.severity === 'warn').length;

  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-ui-text2">Publishing makes v1 available for tenants to adopt. In-progress engagements are unaffected until migrated.</p>

      <div>
        <p className="eyebrow mb-2">Validation</p>
        <div className="space-y-1.5">
          {checks.map((c) => (
            <div key={c.id} className="flex items-start gap-2 text-xs">
              <span className={c.severity === 'pass' ? 'text-success-text' : 'text-warning-text'}>{c.severity === 'pass' ? '✓' : '▲'}</span>
              <div>
                <p className="text-ui-text">{c.label}</p>
                {c.detail && <p className="text-ui-text3">{c.detail}</p>}
              </div>
            </div>
          ))}
        </div>
        <p className={`text-xs mt-3 ${warnings === 0 ? 'text-success-text' : 'text-warning-text'}`}>
          Validation {warnings === 0 ? 'passed' : 'completed'} — 0 blocking issues, {warnings} warning{warnings === 1 ? '' : 's'}. Ready to publish.
        </p>
      </div>

      <div>
        <p className="eyebrow mb-2">Publish summary</p>
        <p className="text-sm font-medium text-ui-text mb-2">{draft.name || 'Untitled workflow'} → v1</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <StatCard label="Stages" value={draft.stages.length} />
          <StatCard label="Statuses" value={draft.statuses.length} />
          <StatCard label="Transitions" value={draft.transitions.length} />
          <StatCard label="Gates" value={draft.governance_gates.length} />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────── Main page ────────────────────────────────────

const WorkflowCreateWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];

  const [draft, setDraft] = useState<WizardDraft>(emptyDraft());
  const [startingPoint, setStartingPoint] = useState<StartingPoint>('blank');
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [copyGates, setCopyGates] = useState(true);
  const [copyAuthority, setCopyAuthority] = useState(true);
  const [copyHooks, setCopyHooks] = useState(true);
  const [created, setCreated] = useState<{ definitionId: string; version: number } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [cloneDefinition] = useCloneWorkflowDefinitionMutation();
  const [createDraft] = useCreateWorkflowDefinitionMutation();
  const [updateDraft] = useUpdateWorkflowDefinitionMutation();
  const [publishDraft] = usePublishWorkflowDefinitionMutation();

  const canContinue = () => {
    if (step.id === 'identify') return !!draft.name && draft.engagement_types.length > 0;
    if (step.id === 'starting_point') return startingPoint === 'blank' || !!cloneSourceId;
    return true;
  };

  const buildRequestBody = (): CreateWorkflowRequest => ({
    name: draft.name,
    description: draft.description,
    engagement_types: draft.engagement_types,
    stages: draft.stages,
    processes: draft.processes,
    statuses: draft.statuses,
    entry_points: [],
    transitions: draft.transitions,
    governance_gates: draft.governance_gates,
    automation_hooks: [],
    authority_rules: draft.authority_rules,
  });

  const goNext = async () => {
    setError('');
    if (step.id === 'starting_point' && startingPoint !== 'blank') {
      const [sourceDefinitionId, sourceVersionStr] = cloneSourceId.split('@');
      setBusy(true);
      try {
        const result = await cloneDefinition({
          definition_id: sourceDefinitionId,
          version: Number(sourceVersionStr),
          name: draft.name,
          copy_governance_gates: copyGates,
          copy_authority_rules: copyAuthority,
          copy_automation_hooks: copyHooks,
        }).unwrap();
        const cloned = result.data;
        setDraft((d) => ({
          ...d,
          stages: cloned.stages as StageDefinition[],
          processes: cloned.processes as ProcessDefinition[],
          statuses: cloned.statuses as StatusDefinition[],
          transitions: cloned.transitions as TransitionDefinition[],
          governance_gates: cloned.governance_gates as GovernanceGate[],
          authority_rules: cloned.authority_rules as AuthorityRule[],
        }));
        setCreated({ definitionId: cloned.definition_id, version: cloned.version });
      } catch (e: unknown) {
        const err = e as { data?: { message?: string } };
        setError(err?.data?.message ?? 'Failed to clone the selected workflow.');
        setBusy(false);
        return;
      }
      setBusy(false);
    }
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const handlePublish = async () => {
    setBusy(true);
    setError('');
    try {
      let definitionId = created?.definitionId;
      let version = created?.version;
      if (created) {
        await updateDraft({ definition_id: created.definitionId, version: created.version, body: buildRequestBody() }).unwrap();
      } else {
        const result = await createDraft(buildRequestBody()).unwrap();
        definitionId = result.data.definition_id;
        version = result.data.version;
      }
      await publishDraft({ definition_id: definitionId!, version: version! }).unwrap();
      navigate(`/platform/workflows/${definitionId}/${version}`);
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      setError(err?.data?.message ?? 'Failed to publish.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <PageHeader
          title={draft.name || 'New workflow'}
          subtitle={
            <>
              <span className="block">
                Workflow Definitions / {stepIndex === 0 ? 'New workflow' : draft.name || 'New workflow'}
              </span>
              <Badge variant="neutral" className="mt-1 uppercase">Draft · unpublished</Badge>
            </>
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {error && <p className="max-w-xs text-xs text-danger-text">{error}</p>}
              <Button variant="outline" size="sm" onClick={() => navigate('/platform/workflows')}>Cancel</Button>
              {stepIndex > 0 && <Button variant="outline" size="sm" onClick={goBack} disabled={busy}>← Back</Button>}
              {step.id === 'validate' ? (
                <Button size="sm" onClick={handlePublish} disabled={busy}>{busy ? 'Publishing…' : 'Publish v1 →'}</Button>
              ) : (
                <Button size="sm" onClick={goNext} disabled={!canContinue() || busy}>{busy ? 'Working…' : 'Continue →'}</Button>
              )}
            </div>
          }
        />
      }
    >
      <div className="flex gap-8">
        <StepRail currentIndex={stepIndex} />
        <div className="card-surface min-w-0 flex-1 p-6">
          <p className="mb-1 text-micro font-semibold uppercase tracking-eyebrow text-accent-text">Step {stepIndex + 1} of {STEPS.length}</p>
          <h2 className="text-base font-semibold text-ui-text mb-4">{step.title}</h2>
          {step.id === 'identify' && <IdentifyStep draft={draft} setDraft={setDraft} />}
          {step.id === 'starting_point' && (
            <StartingPointStep
              startingPoint={startingPoint} setStartingPoint={setStartingPoint}
              cloneSourceId={cloneSourceId} setCloneSourceId={setCloneSourceId}
              copyGates={copyGates} setCopyGates={setCopyGates}
              copyAuthority={copyAuthority} setCopyAuthority={setCopyAuthority}
              copyHooks={copyHooks} setCopyHooks={setCopyHooks}
            />
          )}
          {step.id === 'scaffold' && <ScaffoldStep draft={draft} setDraft={setDraft} />}
          {step.id === 'transitions' && <TransitionsStep draft={draft} setDraft={setDraft} />}
          {step.id === 'validate' && <ValidateStep draft={draft} />}
        </div>
      </div>
    </PageShell>
  );
};

export default WorkflowCreateWizardPage;
